export interface SidebarButton {
  id: string;
  icon: string;
  tooltip: string;
  routeDashboard?: string;
  routeComponent?: string;
}

export const SIDEBAR_BUTTONS: SidebarButton[] = [
  {
    id: 'heat',
    icon: 'assets/imgs/heatwave-sidebar.svg',
    tooltip: 'Heat',
    routeDashboard: 'heatwatch'
  },
  {
    id: 'rainfall',
    icon: 'assets/imgs/rainfall-sidebar.svg',
    tooltip: 'Rainfall',
    routeDashboard: 'nowcastweather',
    routeComponent: 'rainfall-forecast'
  },
  {
    id: 'fog',
    icon: 'assets/imgs/fog.svg',
    tooltip: 'Fog',
    routeDashboard: 'fogwatch'
  },
  {
    id: 'light',
    icon: 'assets/imgs/lightning.svg',
    tooltip: 'Lightning',
    routeDashboard: 'lightning'
  },
  {
    id: 'splitscreen',
    icon: 'assets/imgs/split.svg',
    tooltip: 'Split Screen',
    routeComponent: 'splitscreen'
  },
  {
    id: 'metmind',
    icon: 'assets/imgs/metmind_logo.svg',
    tooltip: 'metMIND',
    routeDashboard: 'metmind'
  },
  {
    id: 'satellite',
    icon: 'assets/imgs/satellite.svg',
    tooltip: 'Satellite',
    routeDashboard: 'satellite'
  },
  {
    id: 'forecast',
    icon: 'assets/imgs/weather_forecast_2.svg',
    tooltip: 'Forecast',
    routeDashboard: 'forecast'
  },
  {
    id: 'urban-heat-island',
    icon: 'assets/imgs/urban-heat-island.svg',
    tooltip: 'Urban Heat Island',
    routeDashboard: 'urban-heat-island'
  },
  {
    id: 'metwiseAlerts',
    icon: 'assets/imgs/sun_RainCutout.svg',
    tooltip: 'metWISE Alerts',
    routeDashboard: 'metwiseAlerts'
  },
  {
    id: 'drought',
    icon: 'assets/imgs/drought.svg',
    tooltip: 'Drought',
    routeDashboard: 'drought'
  },
  {
    id: 'crop-yield',
    icon: 'assets/imgs/crop_yield.svg',
    tooltip: 'Crop Yield',
    routeDashboard: 'crop-yield'
  },
  // {
  //   id: 'sdmp',
  //   icon: 'assets/imgs/district.svg',
  //   tooltip: 'SDMP',
  //   routeDashboard: 'monitoring-plan'
  // }
  // {
  //   id: 'land-subsidence',
  //   icon: 'assets/imgs/land-subsidence.svg',
  //   tooltip: 'Land Subsidence',
  //   routeDashboard: 'land-subsidence'
  // }
];

// =====================================================================================================================
// MODULE MENU (fa-bars drawer): every metWISE module with its sub-modules, in the side bar's order
// =====================================================================================================================
/** loginFlag: localStorage key set to 'true' after that module's login; missing -> loginRoute instead of route */
export interface SubModule {
  type: string;
  label: string;
  icon: string;
  /** thumbnail in the fa-bars module menu (icon stays the gis-dims-map sub-module button image) */
  img: string;
  route: string[];
  loginFlag?: string;
  loginRoute?: string[];
}

// urlSegments: first URL segments that belong to the module (highlights its group in the drawer);
// headed: keep the heading even with a single sub-module (otherwise that is one cell named after the module)
export interface MetwiseModuleGroup {
  id: string;
  title: string;
  icon: string;
  urlSegments: string[];
  items: SubModule[];
  headed?: boolean;
}

// keyed by SIDEBAR_BUTTONS id; the heat / rainfall / fog / lightning / satellite / forecast lists also feed
// the sub-module buttons on the gis-dims-map
const MODULE_MENU: Record<string, { urlSegments: string[]; items: SubModule[] }> = {
  heat: {
    urlSegments: ['heatwatch'],
    items: [
      {
        type: 'heatindex',
        label: 'Feel Like Temp',
        icon: 'assets/imgs/heat_watch_icons/heat_index.jfif',
        img: 'assets/imgs/heat_watch_icons/heat_index.jfif',
        route: ['/heatwatch/heatindex']
      },
      {
        type: 'seamless-temp',
        label: 'Temperature',
        icon: 'assets/imgs/heat_watch_icons/temperature.png',
        img: 'assets/imgs/heat_watch_icons/temperature.png',
        route: ['/heatwatch/seamless-temp']
      },
      {
        type: 'heatwave',
        label: 'Heat Wave',
        icon: 'assets/imgs/heat_watch_icons/heat_wave.jfif',
        img: 'assets/imgs/heat_watch_icons/heat_wave.jfif',
        route: ['/heatwatch/heatwave']
      },
      {
        type: 'heatlog',
        label: 'HeatLog',
        icon: 'assets/imgs/heat_watch_icons/heat_log.svg',
        img: 'assets/imgs/Heatlog.jpg',
        route: ['/heatwatch/heatlog']
      },
      {
        type: 'seasonal-temp',
        label: 'Seasonal-T',
        icon: 'assets/imgs/heat_watch_icons/seasonal_temp.svg',
        img: 'assets/imgs/seasonal-t.jpg',
        route: ['/heatwatch/seasonal-temp']
      }
    ]
  },
  rainfall: {
    urlSegments: ['weatherforecast', 'nowcastweather', 'dryspell'],
    items: [
      {
        type: 'rainfall-forecast',
        label: 'Rainfall',
        icon: 'assets/imgs/rainfall-001.png',
        img: 'assets/imgs/rainfall-001.png',
        route: ['/weatherforecast/rainfall-forecast']
      },
      {
        type: 'nowcast',
        label: 'Nowcast Weather',
        icon: 'assets/imgs/radar.png',
        img: 'assets/imgs/radar.png',
        route: ['/nowcastlogin']
      },
      {
        type: 'dryspell',
        label: 'Dry Spell Monitor',
        icon: 'assets/imgs/heat_watch_icons/dry-soil.jpg',
        img: 'assets/imgs/heat_watch_icons/dry-soil.jpg',
        route: ['/dryspelllogin']
      }
    ]
  },
  fog: {
    urlSegments: ['fogwatch'],
    items: [
      {
        type: 'fogwatch',
        label: 'Fog',
        icon: 'assets/imgs/rainfall-001.png',
        img: 'assets/imgs/rainfall-001.png',
        route: ['/fogwatch/fog'],
        loginFlag: 'isFogUserLoggedIn',
        loginRoute: ['/foglogin']
      }
    ]
  },
  light: {
    urlSegments: ['lightning'],
    items: [
      {
        type: 'lightning',
        label: 'Lightning',
        icon: 'assets/imgs/lightning.svg',
        img: 'assets/imgs/Lightning.jpg',
        route: ['/lightning/light'],
        loginFlag: 'isLightningUserLoggedIn',
        loginRoute: ['/userlogin']
      }
    ]
  },
  splitscreen: {
    urlSegments: ['splitscreen'],
    items: [
      {
        type: 'splitscreen',
        label: 'Split Screen',
        icon: 'assets/imgs/splitscreen-dashboard.png',
        img: 'assets/imgs/splitscreen-dashboard.png',
        route: ['/splitscreen/view']
      }
    ]
  },
  metmind: {
    urlSegments: ['metmind'],
    items: [
      {
        type: 'metmind',
        label: 'metMIND',
        icon: 'assets/imgs/metmind_logo.svg',
        img: 'assets/imgs/metmind_logo.svg',
        route: ['/metmind/landing']
      }
    ]
  },
  satellite: {
    urlSegments: ['satellite'],
    items: [
      {
        type: 'satellite',
        label: 'Satellite',
        icon: 'assets/imgs/satellite.jpg',
        img: 'assets/imgs/Satellite.jpg',
        route: ['/satellite/ctt']
      }
    ]
  },
  forecast: {
    urlSegments: ['forecast'],
    items: [
      {
        type: 'wind-speed',
        label: 'Wind Speed',
        icon: 'assets/imgs/heat_watch_icons/wind-mill.jpg',
        img: 'assets/imgs/heat_watch_icons/wind-mill.jpg',
        route: ['/forecast/wind-speed-forecast']
      },
      {
        type: 'humidity-forecast',
        label: 'Humidity',
        icon: 'assets/imgs/heat_watch_icons/Humidity_icon.svg',
        img: 'assets/imgs//humidity.jpg',
        route: ['/forecast/humidity-forecast']
      },
      {
        type: 'uv-forecast',
        label: 'UV',
        icon: 'assets/imgs/heat_watch_icons/uv-index.svg',
        img: 'assets/imgs/uvindex.jpg',
        route: ['/forecast/uv-forecast']
      }
    ]
  },
  'urban-heat-island': {
    urlSegments: ['urban-heat-island'],
    items: [
      {
        type: 'urban-heat-island',
        label: 'Urban Heat Island',
        icon: 'assets/imgs/urban-heat-island.svg',
        img: 'assets/imgs/urban-heat-island.svg',
        route: ['/urban-heat-island/uhi'],
        loginFlag: 'isUrbanHeatIslandUserLoggedIn',
        loginRoute: ['/urbanHeatIslandLogin']
      }
    ]
  },
  metwiseAlerts: {
    urlSegments: ['metwiseAlerts'],
    items: [
      {
        type: 'metwiseAlerts',
        label: 'metWISE Alerts',
        icon: 'assets/imgs/sun_RainCutout.svg',
        img: 'assets/imgs/sun_RainCutout.svg',
        route: ['/metwiseAlerts/feed'],
        loginFlag: 'isMetwiseAlertsUserLoggedIn',
        loginRoute: ['/metwiseAlertsLogin']
      }
    ]
  },
  drought: {
    urlSegments: ['drought'],
    items: [
      {
        type: 'drought',
        label: 'Drought',
        icon: 'assets/imgs/drought-bg.png',
        img: 'assets/imgs/Drought.jpg',
        route: ['/drought/dm'],
        loginFlag: 'isDroughtUserLoggedIn',
        loginRoute: ['/droughtlogin']
      }
    ]
  },
  'crop-yield': {
    urlSegments: ['crop-yield'],
    items: [
      {
        type: 'crop-yield',
        label: 'Crop Yield',
        icon: 'assets/imgs/crop_yield.svg',
        img: 'assets/imgs/Cropyield.jpg',
        route: ['/crop-yield/cy'],
        loginFlag: 'isCropYieldUserLoggedIn',
        loginRoute: ['/cropyieldlogin']
      }
    ]
  },
  // sdmp: {
  //   urlSegments: ['monitoring-plan'],
  //   items: [
  //     {
  //       type: 'sdmp',
  //       label: 'SDMP',
  //       icon: 'assets/imgs/district.svg',
  //       img: 'assets/imgs/district.svg',
  //       route: ['/monitoring-plan']
  //     }
  //   ]
  // }
};

/** one module as a drawer group: header icon / title from its SIDEBAR_BUTTONS entry */
function moduleGroup(id: string): MetwiseModuleGroup {
  const button = SIDEBAR_BUTTONS.find(b => b.id === id);
  return { id, title: button.tooltip, icon: button.icon, ...MODULE_MENU[id] };
}

/** some of a module's sub-modules, by type */
function pickSubModules(id: string, types: string[]): SubModule[] {
  return MODULE_MENU[id].items.filter(i => types.includes(i.type));
}

// drawer groups in side bar order, except: Weather = Wind Speed + Humidity (from Forecast), Fog and Lightning in one
// group where Fog sits in the side bar (Forecast keeps UV); Satellite moved up under its own heading
export const METWISE_MODULES: MetwiseModuleGroup[] = [
  moduleGroup('heat'),
  moduleGroup('rainfall'),
  {
    id: 'weather',
    title: 'Weather',
    icon: 'assets/imgs/weather_forecast_1.svg',
    urlSegments: ['fogwatch', 'lightning'],
    items: [
      ...pickSubModules('forecast', ['wind-speed', 'humidity-forecast']),
      ...MODULE_MENU.fog.items,
      ...MODULE_MENU.light.items
    ]
  },
  // own heading, right after Weather so the single-module cells below stay together
  {
    ...moduleGroup('satellite'),
    headed: true,
    items: MODULE_MENU.satellite.items.map(i => ({ ...i, label: 'CTT' }))   // menu only: the map's own tile keeps its label
  },
  moduleGroup('splitscreen'),
  moduleGroup('metmind'),
  // /forecast is shared with Weather's Wind Speed / Humidity: highlighted only on the UV page itself
  { ...moduleGroup('forecast'), urlSegments: [], items: pickSubModules('forecast', ['uv-forecast']) },
  moduleGroup('urban-heat-island'),
  moduleGroup('metwiseAlerts'),
  moduleGroup('drought'),
  moduleGroup('crop-yield'),
  // moduleGroup('sdmp')
];

/** copies of one module's sub-modules (callers reorder their own list) */
export function moduleSubModules(id: string): SubModule[] {
  return (MODULE_MENU[id] ? MODULE_MENU[id].items : []).map(i => ({ ...i }));
}

/** where a sub-module tile goes: its login page while that module's login flag is not set */
export function subModuleRoute(item: Pick<SubModule, 'route' | 'loginFlag' | 'loginRoute'>): string[] {
  return item.loginFlag && localStorage.getItem(item.loginFlag) !== 'true' ? item.loginRoute : item.route;
}
